import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Edit,
  Loader2,
  Trash2,
  Plus,
  StickyNote,
  Droplet,
} from "lucide-react";

import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EmptyState } from "@/components/consultation/EmptyState";
import { cn } from "@/lib/utils";

import { AddFoodDialog } from "./add-food-dialog";
import { AddNoteDialog } from "./add-note-dialog";
import { FeedTimePicker } from "./feed-time-picker";
import { NutrientReadings } from "./nutrient-readings";
import { computeEntryKcal } from "./meal-constants";

import {
  useCreateFeedGroup,
  useDeleteFeedGroup,
  useUpdateFeedGroup,
} from "@/hooks/useNutritionEvaluation";
import type { FeedData, FeedEntry, FeedTime } from "@/types/nutrition";

// =============================================================================
// Add Feed Dialog — create or edit a feed-group time
// =============================================================================

interface AddFeedDialogProps {
  open: boolean;
  onClose: () => void;
  appointmentId: string;
  isMealPlan: boolean;
  /** Reserved for future per-plan routing; ignored today. */
  planIndex?: number;
  existingFeedGroup?: FeedTime;
}

export const AddFeedDialog: React.FC<AddFeedDialogProps> = ({
  open,
  onClose,
  appointmentId,
  isMealPlan,
  existingFeedGroup,
}) => {
  const [time, setTime] = useState(existingFeedGroup?.time || "");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // The AlertDialog can cause the parent Dialog to re-render with `existingFeedGroup`
  // briefly undefined. Capture the group in a ref so delete still has the id.
  const feedGroupToDeleteRef = React.useRef<FeedTime | null>(null);

  const createFeedGroup = useCreateFeedGroup(appointmentId, isMealPlan);
  const updateFeedGroup = useUpdateFeedGroup(appointmentId, isMealPlan);
  const deleteFeedGroup = useDeleteFeedGroup(appointmentId, isMealPlan);

  const isEdit = !!existingFeedGroup;

  useEffect(() => {
    if (open) {
      setTime(existingFeedGroup?.time || "");
      feedGroupToDeleteRef.current = existingFeedGroup || null;
    } else if (!showDeleteConfirm) {
      feedGroupToDeleteRef.current = null;
    }
  }, [open, existingFeedGroup, showDeleteConfirm]);

  const handleShowDeleteConfirm = () => {
    feedGroupToDeleteRef.current = existingFeedGroup || null;
    setShowDeleteConfirm(true);
  };

  const handleSubmit = () => {
    if (!time) return;
    if (isEdit && existingFeedGroup) {
      updateFeedGroup.mutate(
        { feedGroupId: existingFeedGroup.id, data: { time } },
        { onSuccess: () => onClose() }
      );
    } else {
      createFeedGroup.mutate({ time }, { onSuccess: () => onClose() });
    }
  };

  const handleDelete = () => {
    const feedGroupToDelete = feedGroupToDeleteRef.current;
    if (!feedGroupToDelete) return;
    deleteFeedGroup.mutate(feedGroupToDelete.id, {
      onSuccess: () => {
        setShowDeleteConfirm(false);
        onClose();
      },
    });
  };

  const isPending =
    createFeedGroup.isPending ||
    updateFeedGroup.isPending ||
    deleteFeedGroup.isPending;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit Feed" : "Add New Feed"}</DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Update the time for this feed."
                : "Set a time for the new feed."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="feed-time">Feed Time</Label>
            <FeedTimePicker
              id="feed-time"
              value={time}
              onChange={setTime}
              disabled={isPending}
            />
          </div>

          <DialogFooter className={cn(isEdit && "sm:justify-between")}>
            {isEdit && (
              <Button
                variant="destructive"
                onClick={handleShowDeleteConfirm}
                disabled={isPending}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )}
            <div className="flex gap-2 sm:ml-auto">
              <Button variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!time || isPending}>
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEdit ? "Update" : "Create"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="border-border/60">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Feed?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this feed? All items in this feed
              will also be deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete();
              }}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Feed
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// =============================================================================
// Feed Time Pill — inline editable feed-group time, used in WidgetHeader title
// =============================================================================

interface FeedTimePillProps {
  feedGroup: FeedTime;
  appointmentId: string;
  isMealPlan: boolean;
  isCompleted: boolean;
}

const FeedTimePill: React.FC<FeedTimePillProps> = ({
  feedGroup,
  appointmentId,
  isMealPlan,
  isCompleted,
}) => {
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState(feedGroup.time || "");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const updateFeedGroup = useUpdateFeedGroup(appointmentId, isMealPlan);
  const deleteFeedGroup = useDeleteFeedGroup(appointmentId, isMealPlan);

  useEffect(() => {
    if (!open) setTime(feedGroup.time || "");
  }, [feedGroup.time, open]);

  const display =
    feedGroup.time && feedGroup.time.length >= 5
      ? feedGroup.time.substring(0, 5)
      : feedGroup.time || "Set time";

  const handleSave = () => {
    if (!time || time === feedGroup.time) {
      setOpen(false);
      return;
    }
    updateFeedGroup.mutate(
      { feedGroupId: feedGroup.id, data: { time } },
      { onSuccess: () => setOpen(false) }
    );
  };

  const handleDelete = () => {
    deleteFeedGroup.mutate(feedGroup.id, {
      onSuccess: () => {
        setShowDeleteConfirm(false);
        setOpen(false);
      },
    });
  };

  if (isCompleted) {
    return feedGroup.time ? (
      <Badge variant="outline" className="text-muted-foreground">
        {display}
      </Badge>
    ) : null;
  }

  const isPending = updateFeedGroup.isPending || deleteFeedGroup.isPending;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Badge
            variant="outline"
            role="button"
            tabIndex={0}
            className="text-muted-foreground cursor-pointer hover:bg-muted/40 gap-1"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
              }
            }}
          >
            {display}
            <Edit className="size-3 text-muted-foreground/70" />
          </Badge>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-2">
            <label className="z-caption text-muted-foreground">Feed time</label>
            <FeedTimePicker
              value={time}
              onChange={setTime}
              disabled={isPending}
            />
            <div className="flex items-center justify-between gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen(false)}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isPending || !time}
                >
                  {updateFeedGroup.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="border-border/60">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Feed?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this feed? All items in this feed
              will also be deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteFeedGroup.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete();
              }}
              disabled={deleteFeedGroup.isPending}
            >
              {deleteFeedGroup.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete Feed
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// =============================================================================
// Tube Feeding Section — shared between Diet Recall and Meal Plan
// =============================================================================

export interface RenderTubeFeedEntryArgs {
  entry: FeedEntry;
  feedGroups: FeedTime[];
  mealGroupLabel: string;
}

interface TubeFeedingSectionProps {
  feedData: FeedData | null;
  appointmentId: string;
  isCompleted: boolean;
  isMealPlan?: boolean;
  planIndex?: number;
  /**
   * Show an inline trash button next to the "No items added yet" hint, with
   * a confirmation AlertDialog. Used by Meal Plan to give a one-click way to
   * remove an empty feed without going through the edit dialog.
   */
  allowDeleteEmptyFeed?: boolean;
  /**
   * Renders a single feed entry. Each tab supplies its own MealEntryRow with
   * the right tab-specific props baked in (e.g. `useNetCarbs`, `planIndex`).
   */
  renderEntry: (args: RenderTubeFeedEntryArgs) => React.ReactNode;
}

export const TubeFeedingSection: React.FC<TubeFeedingSectionProps> = ({
  feedData,
  appointmentId,
  isCompleted,
  isMealPlan = false,
  planIndex,
  allowDeleteEmptyFeed = false,
  renderEntry,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );
  const [showAddFoodForGroup, setShowAddFoodForGroup] = useState<string | null>(
    null
  );
  const [showAddNoteForGroup, setShowAddNoteForGroup] = useState<string | null>(
    null
  );
  const [feedGroupToDelete, setFeedGroupToDelete] = useState<FeedTime | null>(
    null
  );
  const deleteFeedGroup = useDeleteFeedGroup(appointmentId, isMealPlan);

  const selectedFeedGroupForFood = feedData?.feed_groups?.find(
    (fg) => fg.id === showAddFoodForGroup
  );
  const selectedFeedGroupForNote = feedData?.feed_groups?.find(
    (fg) => fg.id === showAddNoteForGroup
  );

  // Sequence feeds in ascending order of time. Groups without a time fall to
  // the end and keep their original relative order.
  const sortedFeedGroups = useMemo(() => {
    if (!feedData?.feed_groups) return [];
    return [...feedData.feed_groups].sort((a, b) => {
      const aTime = a.time || "";
      const bTime = b.time || "";
      if (!aTime && !bTime) return a.order - b.order;
      if (!aTime) return 1;
      if (!bTime) return -1;
      return aTime.localeCompare(bTime);
    });
  }, [feedData?.feed_groups]);

  if (!feedData || !feedData.feed_groups || feedData.feed_groups.length === 0) {
    return (
      <Widget>
        <WidgetContent className="p-6">
          <EmptyState
            icon={
              <Droplet className="h-12 w-12 text-muted-foreground/30 mb-2" />
            }
            title="No feeds created yet"
            subtitle='Click "Add Feed" to create your first feed'
          />
        </WidgetContent>
      </Widget>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {sortedFeedGroups.map((feedGroup, index) => {
          const isOpen = expandedGroups[feedGroup.id] || false;
          const handleToggle = () =>
            setExpandedGroups((prev) => ({
              ...prev,
              [feedGroup.id]: !isOpen,
            }));

          // Tube feed constraints: only one food and only one note per feed.
          // Tracked here because both the title row and the action row need them.
          const hasFoodEntry = feedGroup.entries.some(
            (e) => e.food_item_details || e.feed_item_details
          );
          const hasNoteEntry = feedGroup.entries.some(
            (e) => e.comment && e.comment.trim() !== ""
          );
          const feedLabel = `Feed ${index + 1}`;

          return (
            <Widget key={feedGroup.id}>
              <Collapsible
                open={isOpen}
                onOpenChange={(open) =>
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [feedGroup.id]: open,
                  }))
                }
              >
                <WidgetHeader
                  title=""
                  collapsible
                  expanded={isOpen}
                  onToggle={handleToggle}
                  titleAccessory={
                    <>
                      <Badge variant="secondary" className="font-medium">
                        {feedLabel}
                      </Badge>
                      <FeedTimePill
                        feedGroup={feedGroup}
                        appointmentId={appointmentId}
                        isMealPlan={isMealPlan}
                        isCompleted={isCompleted}
                      />
                    </>
                  }
                  extras={
                    feedGroup.entries.some((e) => !e.is_comment) ? (
                      <NutrientReadings
                        energy={
                          feedGroup.feed_nutrients_summary?.energy ??
                          feedGroup.entries.reduce(
                            (sum, e) => sum + computeEntryKcal(e),
                            0
                          )
                        }
                        protein={feedGroup.feed_nutrients_summary?.protein}
                        carbs={feedGroup.feed_nutrients_summary?.carbs}
                        fat={feedGroup.feed_nutrients_summary?.fat}
                      />
                    ) : undefined
                  }
                />

                <CollapsibleContent>
                  <WidgetContent
                    className={
                      feedGroup.entries.length === 0 ? "py-2" : undefined
                    }
                  >
                    {feedGroup.entries.length > 0 && (
                      <>
                        <Separator className="mb-4" />
                        <div className="space-y-2">
                          {feedGroup.entries
                            .slice()
                            .sort((a, b) => a.order - b.order)
                            .map((entry) =>
                              renderEntry({
                                entry,
                                feedGroups: sortedFeedGroups,
                                mealGroupLabel: feedLabel,
                              })
                            )}
                        </div>
                      </>
                    )}

                    {!isCompleted && (
                      <div
                        className={cn(
                          "flex items-center gap-2",
                          feedGroup.entries.length === 0
                            ? "justify-between"
                            : "justify-end pt-3"
                        )}
                      >
                        {feedGroup.entries.length === 0 && (
                          <span className="z-body text-muted-foreground">
                            No items added yet
                          </span>
                        )}
                        <div className="flex gap-2">
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    size="icon-sm"
                                    onClick={() =>
                                      setShowAddFoodForGroup(feedGroup.id)
                                    }
                                    disabled={hasFoodEntry}
                                    aria-label="Add food"
                                  >
                                    <Plus />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {hasFoodEntry
                                  ? "Only one food item allowed per feed"
                                  : "Add food"}
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    size="icon-sm"
                                    onClick={() =>
                                      setShowAddNoteForGroup(feedGroup.id)
                                    }
                                    disabled={hasNoteEntry || !hasFoodEntry}
                                    aria-label="Add note"
                                  >
                                    <StickyNote />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {hasNoteEntry
                                  ? "Only one note allowed per feed"
                                  : !hasFoodEntry
                                    ? "Add a food item before adding a note"
                                    : "Add note"}
                              </TooltipContent>
                            </Tooltip>
                            {allowDeleteEmptyFeed && !hasFoodEntry && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="icon-sm"
                                    variant="destructive"
                                    onClick={() =>
                                      setFeedGroupToDelete(feedGroup)
                                    }
                                    aria-label="Delete feed"
                                  >
                                    <Trash2 />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete feed</TooltipContent>
                              </Tooltip>
                            )}
                          </TooltipProvider>
                        </div>
                      </div>
                    )}
                  </WidgetContent>
                </CollapsibleContent>
              </Collapsible>
            </Widget>
          );
        })}
      </div>

      {selectedFeedGroupForFood && (
        <AddFoodDialog
          open={!!showAddFoodForGroup}
          onClose={() => setShowAddFoodForGroup(null)}
          mealGroupId={selectedFeedGroupForFood.id}
          mealTimeLabel={selectedFeedGroupForFood.time || ""}
          appointmentId={appointmentId}
          isTubeFeeding={true}
          isMealPlan={isMealPlan}
          planIndex={planIndex}
          feedEntries={selectedFeedGroupForFood.entries}
          mealGroupOptions={sortedFeedGroups
            .filter(
              (fg) =>
                fg.id === selectedFeedGroupForFood.id ||
                !fg.entries.some(
                  (e) => e.food_item_details || e.feed_item_details
                )
            )
            .map((fg) => ({
              id: fg.id,
              label: fg.label || fg.time || "Feed",
            }))}
        />
      )}

      {selectedFeedGroupForNote && (
        <AddNoteDialog
          open={!!showAddNoteForGroup}
          onClose={() => setShowAddNoteForGroup(null)}
          mealGroupId={selectedFeedGroupForNote.id}
          mealTimeLabel={selectedFeedGroupForNote.time || ""}
          appointmentId={appointmentId}
          isTubeFeeding={true}
          isMealPlan={isMealPlan}
          feedEntries={selectedFeedGroupForNote.entries}
        />
      )}

      {allowDeleteEmptyFeed && (
        <AlertDialog
          open={!!feedGroupToDelete}
          onOpenChange={(open) => {
            if (!open) setFeedGroupToDelete(null);
          }}
        >
          <AlertDialogContent className="border-border/60">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Feed?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this feed? This action cannot be
                undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteFeedGroup.isPending}>
                Cancel
              </AlertDialogCancel>
              <Button
                type="button"
                variant="destructive"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!feedGroupToDelete) return;
                  deleteFeedGroup.mutate(feedGroupToDelete.id, {
                    onSuccess: () => setFeedGroupToDelete(null),
                  });
                }}
                disabled={deleteFeedGroup.isPending}
              >
                {deleteFeedGroup.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Delete Feed
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};
